/**
 * StatusBox.jsx
 * Phase 9a.2 Step 3 — Interactive Control Component
 *
 * A status indicator box showing current processing state.
 * Colors resolved from DESIGN_INTENTS for zero-hex compliance.
 * Law: PKG-009 UI Capabilities, PKG-019 End-to-End Surface Visibility
 * Authority: UI_DESIGN_CONTRACT_V3.md, Visual Law (Law 3)
 *
 * Zero inline hex codes. All colors from DESIGN_INTENTS tokens.
 * Business language only — labels provided by caller.
 */

import React from 'react';
import { DESIGN_INTENTS } from '@/config/ui-invariants';
import { cn } from '@/lib/utils';

/**
 * StatusBox
 *
 * Props:
 *   intent      (string)          — key into DESIGN_INTENTS (primary, positive, negative, warning, info, neutral, muted)
 *   label       (string)          — primary status label shown to user
 *   description (string)          — optional supporting description
 *   icon        (React component) — optional Phosphor icon component
 *   className   (string)          — optional additional classes on the wrapper
 *
 * Example:
 *   <StatusBox intent="info" label="Analysis in progress" description="Scanning 3 articles..." icon={Spinner} />
 */
export default function StatusBox({
    intent = 'neutral',
    label,
    description,
    icon: IconComponent,
    className,
}) {
    // Resolve intent — fall back to 'neutral' if unknown key provided
    const intentStyle = DESIGN_INTENTS[intent] || DESIGN_INTENTS.neutral;

    return (
        <div
            className={cn(
                'rounded-xl p-4 border',
                intentStyle.bg,
                intentStyle.border,
                className,
            )}
            role="status"
            aria-live="polite"
        >
            <div className="flex items-start gap-3">
                {/* Optional icon */}
                {IconComponent && (
                    <span className={cn('flex-shrink-0 mt-0.5', intentStyle.text)}>
                        <IconComponent size={24} aria-hidden="true" />
                    </span>
                )}

                {/* Text area */}
                <div className="flex-1 min-w-0">
                    {/* Label — always bold, minimum 15px */}
                    <p className={cn('font-semibold text-[15px]', intentStyle.text)}>
                        {label}
                    </p>

                    {/* Description — slightly lighter via opacity */}
                    {description && (
                        <p className={cn('text-sm mt-0.5 opacity-80', intentStyle.text)}>
                            {description}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
